import type { FC } from 'react';
import { useCallback, useRef, useState } from 'react';
import { useBitable } from 'src/bitable/context';
import { Input } from 'src/common/components/input';
import { ListView } from 'src/common/components/list-view';
import type { ListItem } from 'src/common/components/list-view/types';
import { ListItemType } from 'src/common/components/list-view/types';
import { useOpenModal } from 'src/common/components/next-modal';
import { useUpdatePropertySchema } from 'src/hooks/block/use-update-property-schema';
import { getState } from 'src/redux/store';
import { usePickBlock } from 'src/utils/pick-block';
import { useAppTranslation } from '@flowus/common/i18n/use-translation';
import { appI18n } from '@flowus/common/i18n';

interface FormatProps {
  zh: string;
  en: string;
  pingyin: string;
  symbol?: string;
  example?: string;
}

const getNumberFormat = (): FormatProps[] => {
  return [
    {
      zh: appI18n.t('common.number'),
      en: 'number',
      pingyin: 'shuzi',
      example: '123',
    },
    {
      zh: appI18n.t('bitable.table_view.widgets.number_format.integer'),
      en: 'integer',
      pingyin: 'zhengshu',
      example: '123',
    },
    {
      zh: appI18n.t('bitable.table_view.widgets.number_format.one_decimal'),
      en: 'oneDecimal',
      pingyin: 'yiweixiaoshu',
      example: '1.0',
    },
    {
      zh: appI18n.t('bitable.table_view.widgets.number_format.two_decimal'),
      en: 'twoDecimal',
      pingyin: 'liangweixiaoshu',
      example: '1.00',
    },
    {
      zh: appI18n.t('bitable.table_view.widgets.number_format.thousandth'),
      en: 'thousandth',
      pingyin: 'qianfenwei',
      example: '1,000',
    },
    {
      zh: appI18n.t('bitable.table_view.widgets.number_format.percent_one_decimal'),
      en: 'percentOneDecimal',
      pingyin: 'baifenshuyiweixiaoshu',
      example: '1.0%',
    },
    {
      zh: appI18n.t('bitable.table_view.widgets.number_format.percent_two_decimal'),
      en: 'percent',
      pingyin: 'baifenshuliangweixiaoshu',
      example: '1.00%',
    },
    {
      zh: appI18n.t('bitable.table_view.widgets.number_format.rmb'),
      en: 'rmb',
      pingyin: 'renminbi',
      symbol: 'CN¥',
      example: 'CN¥100.00',
    },
    {
      zh: appI18n.t('bitable.table_view.widgets.number_format.dollar'),
      en: 'dollar',
      pingyin: 'meiyuan',
      symbol: 'US$',
      example: 'US$100.00',
    },
    {
      zh: appI18n.t('bitable.table_view.widgets.number_format.yen'),
      en: 'yen',
      pingyin: 'riyuan',
      symbol: 'JP¥',
      example: 'JP¥100.00',
    },
    {
      zh: appI18n.t('bitable.table_view.widgets.number_format.euro'),
      en: 'euro',
      pingyin: 'ouyuan',
      symbol: '€',
      example: '€100.00',
    },
    {
      zh: appI18n.t('bitable.table_view.widgets.number_format.rub'),
      en: 'rub',
      pingyin: 'lubu',
      symbol: '₽',
      example: '₽100.00',
    },
  ];
};

export const NumberFormat: FormatProps[] = getNumberFormat();

export const useOpenNumberFormat = () => {
  const { readonly } = useBitable();
  const openModal = useOpenModal();

  const openNumberFormat = (event: React.MouseEvent, propertyId: string) => {
    event.stopPropagation();
    if (readonly) return;

    openModal.dropdown({
      popcorn: event.currentTarget,
      placement: 'bottom',
      content: ({ onCloseModal }) => (
        <SelectNumberFormat closeModal={onCloseModal} propertyId={propertyId} />
      ),
    });
  };

  return useCallback(openNumberFormat, [openModal, readonly]);
};
interface Props {
  closeModal?: () => void;
  propertyId: string;
}
export const SelectNumberFormat: FC<Props> = (props) => {
  const { t } = useAppTranslation();
  const { closeModal, propertyId } = props;
  const { collectionId } = useBitable();
  const [keyword, setKeyword] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const updatePropertySchema = useUpdatePropertySchema();

  const collection = usePickBlock(collectionId, ['data'], ['schema']);
  const { numberFormat } = collection?.data.schema?.[propertyId] ?? {};

  const items: ListItem<any>[] = [];
  const regExp = new RegExp(keyword, 'i');

  NumberFormat.forEach((item, index) => {
    const str = item.en + item.zh + item.pingyin;
    if (index === 7) {
      items.push({
        type: ListItemType.LINE,
      });
    }

    if (str.search(regExp) > -1) {
      items.push({
        type: ListItemType.OPERABLE_BLOCK_TEXT,
        data: {
          title: item.zh,
          followTitle: true,
          selected: numberFormat === item.en,
          desc: item.example,
          ...item,
        },
      });
    }
  });

  return (
    <div className="w-[220px] py-2 next-modal">
      <Input
        autoFocus
        inputRef={inputRef}
        placeholder={t('bitable.table_view.widgets.search_format')}
        className="h-8 flex-1 border-grey6 rounded-sm m-2"
        inputClassName="px-2"
        onEnter={() => closeModal?.()}
        onChange={(text) => setKeyword(text)}
      />
      <ListView
        className="overflow-auto max-h-120"
        items={items}
        onItemClick={(item) => {
          closeModal?.();
          const { blocks } = getState();
          const { numberFormat } = blocks[collectionId]?.data.schema?.[propertyId] ?? {};

          if (numberFormat === item.data.title) return;
          updatePropertySchema(
            collectionId,
            propertyId,
            {
              numberFormat: item.data.en,
            },
            {
              needTransaction: true,
              isUpdateNumberFormat: true,
            }
          );
        }}
      />
    </div>
  );
};
